/**
 * Mapping preview helpers — mirrors the backend conversion logic so the wizard
 * can show what will be created before anything is sent.
 */

import { Countermeasure, DesignChange, FailureMode } from '../../../types';

const IMPACT_TO_CLASSIFICATION: Record<string, string[]> = {
  Safety: ['safety'],
  Quality: ['performance', 'reliability'],
  Manufacturing: ['function', 'cost'],
  Design: ['performance', 'function'],
  Cost: ['cost'],
  Maintenance: ['maintenance'],
};

const SEVERITY_TO_METHODS: Record<string, string[]> = {
  critical: ['analysis', 'pre_test', 'actual_confirm', 'dr1'],
  high: ['design_calc', 'analysis', 'actual_confirm'],
  medium: ['design_calc', 'dr1'],
  low: ['dr1', 'knowhow'],
};

export interface PreviewItem {
  itemName: string;
  specification: string;
  classifications: string[];
  standardTarget: string;
  methods: string[];
  severity: string;
}

export function generatePreviewItems(
  selectedFMs: FailureMode[],
  designChanges: DesignChange[],
  countermeasures: Countermeasure[],
): PreviewItem[] {
  const dcMap = new Map(designChanges.map((dc) => [dc.id, dc]));
  const cmByFM = new Map<string, Countermeasure[]>();
  for (const cm of countermeasures) {
    if (cm.failureModeId) {
      const list = cmByFM.get(cm.failureModeId) || [];
      list.push(cm);
      cmByFM.set(cm.failureModeId, list);
    }
  }

  return selectedFMs.map((fm) => {
    const dc = dcMap.get(fm.designChangeId || '');
    const fmCMs = (cmByFM.get(fm.id) || []).sort(
      (a, b) => a.priority - b.priority,
    );

    const classifications: string[] = [];
    for (const area of dc?.impactAreas || []) {
      for (const cls of IMPACT_TO_CLASSIFICATION[area] || []) {
        if (!classifications.includes(cls)) classifications.push(cls);
      }
    }
    if (classifications.length === 0) classifications.push('performance');

    const itemName =
      fm.failureDescription.length > 60
        ? fm.failureDescription.slice(0, 57) + '...'
        : fm.failureDescription;

    return {
      itemName,
      specification: dc?.newSpecification || '',
      classifications,
      standardTarget: fmCMs[0]?.description || '',
      methods: SEVERITY_TO_METHODS[fm.severity] || ['dr1'],
      severity: fm.severity,
    };
  });
}
